import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Trash2, EyeOff, Eye, GripVertical, Upload, Instagram } from "lucide-react";
import { api, API, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";

export default function Gallery() {
  const [list, setList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragId, setDragId] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(() => api.get("/admin/gallery").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let ok = 0;
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        await api.post("/admin/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    }
    setUploading(false);
    if (ok) toast.success(`${ok} foto('s) toegevoegd.`);
    if (fileRef.current) fileRef.current.value = "";
    load();
  };

  const toggleVisible = async (g) => {
    await api.put(`/admin/gallery/${g.id}`, { visible: !(g.visible !== false) });
    load();
  };

  const remove = async (g) => {
    if (!window.confirm("Deze foto verwijderen?")) return;
    await api.delete(`/admin/gallery/${g.id}`);
    toast.success("Foto verwijderd."); load();
  };

  const persistOrder = async (ordered) => {
    setList(ordered);
    try { await api.put("/admin/gallery/reorder", { ids: ordered.map((g) => g.id) }); }
    catch { toast.error("Volgorde opslaan mislukt."); load(); }
  };
  const onDrop = (target) => {
    if (!dragId || dragId === target.id) { setDragId(null); return; }
    const dragged = list.find((g) => g.id === dragId);
    const without = list.filter((g) => g.id !== dragId);
    const idx = without.findIndex((g) => g.id === target.id);
    without.splice(idx, 0, dragged);
    setDragId(null);
    persistOrder(without);
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
        <h1 className="text-3xl font-medium tracking-tight">Instagram / Galerij</h1>
        <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-gallery-btn"
          className="rounded-full bg-brand hover:bg-brand-hover text-white">
          <Upload className="w-4 h-4 mr-1" /> {uploading ? "Uploaden…" : "Foto's uploaden"}
        </Button>
        <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onFiles} data-testid="gallery-file-input" />
      </div>
      <p className="text-ink-muted mb-8">Deze foto's verschijnen als sfeerblok op de homepage. Sleep om de volgorde te wijzigen.</p>

      {!list.length && (
        <div className="bg-white rounded-2xl border border-[#E8E2D9] p-10 text-center text-ink-muted">
          <Instagram className="w-8 h-8 mx-auto mb-3 text-brand/60" strokeWidth={1.5} />
          Nog geen foto's. Upload je mooiste salonfoto's.
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {list.map((g) => (
          <div key={g.id} data-testid={`gallery-${g.id}`}
            draggable onDragStart={() => setDragId(g.id)} onDragEnd={() => setDragId(null)}
            onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(g)}
            className={`group relative rounded-2xl overflow-hidden border border-[#E8E2D9] bg-white aspect-square ${dragId === g.id ? "opacity-50" : ""} ${g.visible === false ? "opacity-60" : ""}`}>
            <img src={`${API}/public/gallery/${g.id}/image`} alt={g.caption || ""} className="w-full h-full object-cover" />
            <span className="absolute top-2 left-2 cursor-grab active:cursor-grabbing bg-white/85 rounded-lg p-1 text-ink-muted" data-testid={`drag-gallery-${g.id}`}><GripVertical className="w-4 h-4" /></span>
            <div className="absolute top-2 right-2 flex gap-1">
              <button onClick={() => toggleVisible(g)} data-testid={`toggle-gallery-${g.id}`} title={g.visible === false ? "Tonen" : "Verbergen"}
                className="bg-white/85 rounded-lg p-1.5 text-ink hover:text-brand">
                {g.visible === false ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              <button onClick={() => remove(g)} data-testid={`delete-gallery-${g.id}`} title="Verwijderen"
                className="bg-white/85 rounded-lg p-1.5 text-destructive hover:bg-destructive hover:text-white">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            {g.visible === false && <span className="absolute bottom-2 left-2 text-[10px] bg-ink/80 text-white rounded-full px-2 py-0.5">Verborgen</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
